import { useEffect, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Ловушка фокуса для диалогов: фокус внутрь при открытии, Tab по кругу, возврат фокуса при закрытии (NFR-10). */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  initialFocus?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;
    const previous = document.activeElement as HTMLElement | null;

    const focusables = () =>
      Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
    const target = initialFocus?.current ?? focusables()[0] ?? root;
    // После анимации появления фокус ставится на первый элемент или сам диалог.
    const raf = requestAnimationFrame(() => target.focus({ preventScroll: true }));

    const onKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        root.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const current = document.activeElement as HTMLElement | null;
      if (event.shiftKey && (current === first || !root.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };
    root.addEventListener('keydown', onKeydown);

    return () => {
      cancelAnimationFrame(raf);
      root.removeEventListener('keydown', onKeydown);
      if (previous && typeof previous.focus === 'function' && document.contains(previous))
        previous.focus({ preventScroll: true });
    };
  }, [ref, active, initialFocus]);
}
